import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
// import crashlytics from '@react-native-firebase/crashlytics';
import { AxiosError } from 'axios';
import { getUserRequest, saveUserConfigRequest } from '@app/api/user';
import { IErrorData } from '../../interfaces/apiError';
import { IUser, IUserConfigItem } from '@app/interfaces/user';
import { logout } from './token';
import { setSentryUser } from '@app/services/sentryService';
import { defaultAcl } from './defaultAcl';
import { RootState } from '../store';
import { getConfig } from '@app/services/configService';

/**
 * Local testing escape hatch: when set, the full `defaultAcl` replaces whatever ACL the API
 * returns, so every module/feature is visible. Set `REACT_APP_FORCE_DEFAULT_ACL=true` in
 * `.env.development.local` (gitignored) rather than editing this file.
 */
const FORCE_DEFAULT_ACL = process.env.REACT_APP_FORCE_DEFAULT_ACL === 'true';

export interface IState {
  loading: boolean;
  userData: IUser;
}

export const initialState = {
  loading: false,
  userData: {},
} as IState;

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ========= Fetch user start =========
      .addCase(fetchUser.pending, (state: IState) => ({
        ...state,
        loading: true,
      }))
      .addCase(
        fetchUser.fulfilled,
        (state: IState, { payload }: { payload: { userData: IUser; lastUserId?: number } }) => ({
          ...state,
          loading: false,
          userData: {
            ...payload.userData,
            personal_number: payload.userData.personal_number ? payload.userData.personal_number : '001', // TODO: FIX this after api changes
          },
          lastUserId: payload.userData.id,
        }),
      )
      .addCase(fetchUser.rejected, (state: IState) => ({
        ...state,
        loading: false,
        userData: initialState.userData,
      }))
      // ========= Fetch user end =========
      // ========= Save user config =========
      .addCase(saveUserConfigAsync.fulfilled, (state: IState, { payload }) => {
        const { key, value } = payload;
        const existingConfig = state.userData.config || [];
        const updatedConfig = existingConfig.map((item) => (item.key === key ? { ...item, value } : item));

        if (!existingConfig.find((item) => item.key === key)) {
          updatedConfig.push({ key, value });
        }

        state.userData.config = updatedConfig;
      })
      // ========= Logout =========
      .addCase(logout.fulfilled, (state: IState) => ({
        ...state,
        loading: initialState.loading,
        userData: initialState.userData,
      }))
      .addCase(logout.rejected, (state: IState) => ({
        ...state,
        loading: initialState.loading,
        userData: initialState.userData,
      }));
  },
});

export const fetchUser = createAsyncThunk('fetchUser', async (_: undefined, { rejectWithValue }) => {
  // crashlytics().log('Get api user data request start.');
  try {
    const response = await getUserRequest();
    const userData = response.data as IUser;

    // crashlytics().log('User signed in.');
    // await Promise.all([crashlytics().setUserId(`${userData.id}`)]);
    setSentryUser({ id: userData.id });

    if (!userData.acl || FORCE_DEFAULT_ACL) {
      userData.acl = defaultAcl;
    }

    // const dashboardModule = userData.acl.modules.find((m) => m.name === 'fishven-admin-tool');
    // const defaultDashboardModule = defaultAcl.modules.find((m) => m.name === 'fishven-admin-tool');
    // if (dashboardModule && defaultDashboardModule) {
    //   for (const featureName of ['hunters', 'huntingLicenses']) {
    //     const defaultFeature = defaultDashboardModule.features.find((f) => f.name === featureName);
    //     if (defaultFeature) {
    //       const idx = dashboardModule.features.findIndex((f) => f.name === featureName);
    //       if (idx !== -1) {
    //         dashboardModule.features[idx] = defaultFeature;
    //       } else {
    //         dashboardModule.features.push(defaultFeature);
    //       }
    //     }
    //   }
    // }

    // if (!userData.acl) {
    // userData.acl.modules[2] = defaultAcl.modules[0];
    // }
    //  userData.acl.modules[0].features[1].views[2].data.overrides[24].condition[0].value = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15];
    // userData.acl.modules[0].features[1].views[2].data.overrides[7].condition[0].value = [2]

    return { userData };
  } catch (error) {
    const err = error as AxiosError;
    // crashlytics().log("Error while trying to get user's data.");
    // crashlytics().recordError(error as Error);
    return rejectWithValue(err.response?.data as IErrorData);
  }
});

export const saveUserConfigAsync = createAsyncThunk(
  'user/saveConfig',
  async ({ key, value }: { key: string; value: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const currentConfig = state.user.userData.config ?? [];

      const updatedConfig: IUserConfigItem[] = [...currentConfig.filter((item) => item.key !== key), { key, value }];

      await saveUserConfigRequest(updatedConfig);

      return { key, value };
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err.response?.data as IErrorData);
    }
  },
);

export const saveTableConfigAsync = createAsyncThunk(
  'user/saveTableConfig',
  async (
    {
      tableKey,
      columns,
    }: {
      tableKey?: string;
      columns: { dataIndex: string; key: string; hidden: boolean }[];
    },
    { dispatch },
  ) => {
    if (!tableKey) return;

    const key = `tableConfig_${tableKey}`;
    const value = JSON.stringify(columns);

    dispatch(saveUserConfigAsync({ key, value }));

    return { tableKey, columns };
  },
);

// export const { } = slice.actions;

export const user = slice.reducer;

export const userIdSelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.id,
);

export const userConfigurationSelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.configuration.apiConfig,
);

export const observationMaxImagesLength = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.configuration?.observationMaxImagesLength || 5,
);

export const showPrintBvrButton = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.configuration?.showPrintBvrButton ?? false,
);

export const userDefaultCurrencySelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.configuration?.defaultCurrency || 'CHF',
);

export const inspectionProcessTypeSelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => {
    const override = getConfig().inspectionProcessTypeOverride;
    return override ?? (userData.configuration?.inspectionProcessType || 1);
  },
);

export const aclSelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.acl,
);

export const organizationEntitiesSelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.organizationEntities,
);

export const isProfessionalFisherSelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.isProfessionalFisher ?? false,
);

export const dashboardAclSelector = createSelector(aclSelector, (acl) =>
  acl?.modules.find((module) => module.name === 'fishven-admin-tool'),
);

export const tabletInventoriesAclSelector = createSelector(aclSelector, (acl) =>
  acl?.modules
    .find((module) => module.name === 'fishven-admin')
    ?.features.find((feature) => feature.name === 'inventories'),
);

export const dashboardHomeAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'dashboardHomeView'),
);

// Fishing Operations
export const licensesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'licenses'),
);

export const licensesReturnAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'licensesReturn'),
);

export const fishersAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'fishers'),
);

export const sessionsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'sessions'),
);

export const catchesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'catches'),
);

export const inspectionsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'inspections'),
);

export const paperStatisticsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'paperStatistics'),
);

// Fishing Management
export const inventoryAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'inventory'),
);

export const naturalBreedingAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'naturalBreeding'),
);

export const stockingPlanAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'stocking-plan'),
);

export const stockingJournalAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'stocking-journal'),
);

export const stockingYearlySummaryAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'stocking-yearly-summary'),
);

export const breedingJournalAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'breedingJournal'),
);

// Fishing Habitat
export const fishMovementAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'fishMovement'),
);

export const observationsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'observations'),
);

export const draughtinessAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'draughtiness'),
);

export const waterQualityAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'waterQuality'),
);

// Professional Fishers
export const professionalFisherAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'professionalFisher'),
);

export const allProfessionalFishersAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'allProfessionalFishers'),
);

// Hunting Operations
export const huntersAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'hunters'),
);

export const gamesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'games'),
);

export const huntingGamekeeperSelector = (state: RootState) => state.user.userData.huntingGamekeeper ?? false;
export const huntingAreaManagerSelector = (state: RootState) => state.user.userData.huntingAreaManager ?? false;
export const huntingAccountingSelector = (state: RootState) => state.user.userData.huntingAccounting ?? false;
export const huntingAllowEditingSelector = (state: RootState) =>
  state.user.userData.TMP_ALLOWING_HUNTING_EDITING ?? false;

export const huntingInspectionsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'huntingInspections'),
);

export const huntingLicensesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'huntingLicenses'),
);

// Analytics
export const analyticsFishingOperationsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'analyticsFishingOperations'),
);

export const analyticsFishingManagementAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'analyticsFishingManagement'),
);

export const analyticsFishingHabitatAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'analyticsFishingHabitat'),
);

export const analyticsProfessionalFishersAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'analyticsProfessionalFishers'),
);

export const analyticsHuntingOperationsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'analyticsHuntingOperations'),
);

export const analyticsSummaryAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'analyticsSummary'),
);

export const analyticsBasicStatisticsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'analyticsBasicStatistics'),
);

//User Management
export const rolesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'roles'),
);

export const organisationalUnitsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'organisationalUnits'),
);

export const inboxMessagesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'inboxMessages'),
);

// Master Data

export const waterBodiesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'waterbodies'),
);

export const waterbodyRegulationsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'waterbodyRegulations'),
);

export const geographicAreasAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'geographicAreas'),
);

export const licenseTypesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'licenseTypes'),
);

export const fishTypesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'fishTypes'),
);

export const netTypesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'netTypes'),
);

export const fishingFeesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'fishingFees'),
);

export const huntingFeesAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'huntingFees'),
);

// Reports
export const reportsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'reports'),
);

// Settings
export const generalSettingsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'generalSettings'),
);

export const workflowsAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'workflowsSettings'),
);

export const adminUsersAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'adminUsers'),
);

/** Self-service profile (/profile/personal-info): `permission.edit === true` unlocks full AdminUserModal fields. */
export const profileAclSelector = createSelector(dashboardAclSelector, (dashboardAcl) =>
  dashboardAcl?.features.find((feature) => feature.name === 'profile'),
);

export const salesPointsSelector = createSelector(
  (state: { user: IState }) => state.user.userData,
  (userData) => userData.salesPoints || [],
);

export const tableConfigSelector = (tableKey?: string) =>
  createSelector(
    (state: { user: IState }) => state.user.userData.config,
    (config) => {
      const configItem = config?.find((item) => item.key === `tableConfig_${tableKey}`);
      return configItem ? JSON.parse(configItem.value) : [];
    },
  );
